import { useEffect, useRef, useState } from 'react';

import { api } from '../../services/api';
import type { Chat, ContactMemory } from '../../types';

type Props = {
  activeChat: Chat;
};

const sentimentLabel = (score: number): { label: string; color: string } => {
  if (score >= 70) return { label: 'Positief', color: 'positive' };
  if (score >= 40) return { label: 'Neutraal', color: 'neutral' };
  if (score > 0) return { label: 'Negatief', color: 'negative' };
  return { label: 'Onbekend', color: 'unknown' };
};

const ContactMemoryPanel = ({ activeChat }: Props) => {
  const [memory, setMemory] = useState<ContactMemory | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const loadedChatId = useRef<string | null>(null);

  useEffect(() => {
    if (loadedChatId.current === activeChat.id) return;
    loadedChatId.current = activeChat.id;
    setMemory(null);
  }, [activeChat.id]);

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !memory && !loading) {
      setLoading(true);
      void api.contactMemory(activeChat.id)
        .then(setMemory)
        .catch(() => undefined)
        .finally(() => setLoading(false));
    }
  };

  const sentiment = memory ? sentimentLabel(memory.sentimentScore) : null;

  return (
    <div className={`contact-memory-panel ${open ? 'open' : ''}`}>
      <button
        type="button"
        className="memory-toggle-btn"
        onClick={handleToggle}
        aria-expanded={open}
      >
        <span className="memory-toggle-icon" aria-hidden="true">🧠</span>
        <span className="memory-toggle-label">CRM Memory</span>
        {memory && sentiment && (
          <span className={`sentiment-pill ${sentiment.color}`}>
            {sentiment.label}
          </span>
        )}
        <span className="memory-chevron">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="memory-content">
          {loading && <p className="muted small">Laden…</p>}
          {!loading && !memory && (
            <p className="muted small">Nog geen memory voor dit contact.</p>
          )}
          {memory && (
            <>
              <div className="memory-section">
                <h4>Sentiment</h4>
                <div className="sentiment-bar">
                  <div
                    className={`sentiment-fill ${sentiment?.color ?? ''}`}
                    style={{ width: `${memory.sentimentScore}%` }}
                  />
                </div>
                <span className="muted small">{memory.sentimentScore}/100 — {sentiment?.label}</span>
              </div>

              <div className="memory-section">
                <h4>Taal</h4>
                <span className="memory-tag">{memory.language}</span>
              </div>

              <div className="memory-section">
                <h4>Toon profiel</h4>
                <div className="memory-tags">
                  <span className="memory-tag">{memory.toneProfile.formality}</span>
                  <span className="memory-tag">{memory.toneProfile.energy} energy</span>
                  <span className="memory-tag">{memory.toneProfile.emojiUsage} emoji</span>
                </div>
              </div>

              {memory.importantFacts.length > 0 && (
                <div className="memory-section">
                  <h4>Belangrijke feiten</h4>
                  <ul className="memory-list">
                    {memory.importantFacts.slice(0, 8).map((fact, i) => (
                      <li key={i}>{fact}</li>
                    ))}
                  </ul>
                </div>
              )}

              {memory.topicHistory.length > 0 && (
                <div className="memory-section">
                  <h4>Onderwerpen</h4>
                  <div className="memory-tags">
                    {memory.topicHistory.slice(0, 10).map((topic, i) => (
                      <span key={i} className="memory-tag">{topic}</span>
                    ))}
                  </div>
                </div>
              )}

              {memory.openTopics.length > 0 && (
                <div className="memory-section">
                  <h4>Open onderwerpen</h4>
                  <div className="memory-tags">
                    {memory.openTopics.map((topic, i) => (
                      <span key={i} className="memory-tag open">{topic}</span>
                    ))}
                  </div>
                </div>
              )}

              {memory.conversationSummaries.length > 0 && (
                <div className="memory-section">
                  <h4>Gesprek samenvattingen</h4>
                  <ul className="memory-list">
                    {memory.conversationSummaries.slice(-3).map((summary, i) => (
                      <li key={i}>{summary}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="memory-section">
                <h4>Statistieken</h4>
                <div className="memory-stats">
                  <span className="memory-stat">
                    <strong>{memory.messageCount}</strong> berichten
                  </span>
                  <span className="memory-stat">
                    <strong>{memory.incomingCount}</strong> inkomend
                  </span>
                  {memory.responseTimes.samples.length > 0 && (
                    <span className="memory-stat">
                      <strong>{Math.round(memory.responseTimes.avgSeconds)}s</strong> gem. reactie
                    </span>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ContactMemoryPanel;
